/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React from 'react';
import { PropTypes } from 'prop-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tag,
} from '@jem-open/jem-ui';
import { Calendar, User } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { formatDateTime } from '../../utils/formatters';

const EducationQuestionCard = ({
  platform = '',
  category = '',
  template = '',
  isActive = false,
  answers = [],
  questionType = '',
  questionId = '',
  dateAdded = '',
  dateModified = '',
  modifiedBy = '',
  addedBy = null,
}) => {
  const history = useHistory();
  const hasAnswers = Array.isArray(answers) && answers.length > 0;

  return (
    <div className="mb-sm rounded-xl border border-greyscale-border-default bg-white p-md shadow-sm">
      <div className="mb-xs flex flex-wrap items-center gap-xxs">
        <Tag variant={isActive ? 'success' : 'drafted'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
        {platform && <Tag variant="processing">{platform}</Tag>}
        {category && <Tag variant="purple">{category}</Tag>}
      </div>

      <p className="mb-sm whitespace-pre-line text-sm text-greyscale-text-body">
        {template}
      </p>

      <div className="flex flex-wrap items-center gap-xs">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            history.push({
              pathname: `/admin/question/education/${questionId}`,
              state: {
                questionId,
                template,
                platform,
                questionType,
                category,
                answers,
                isActive,
              },
            })
          }>
          Edit
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm">
              Metadata
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="text-sm">
            <dl className="grid grid-cols-[auto,1fr] gap-x-sm gap-y-xxs">
              <dt className="text-greyscale-text-caption">Date added</dt>
              <dd className="text-greyscale-text-body">
                {formatDateTime(dateAdded)}
              </dd>
              <dt className="text-greyscale-text-caption">Added by</dt>
              <dd className="text-greyscale-text-body">{addedBy ?? '—'}</dd>
              {dateModified && (
                <>
                  <dt className="text-greyscale-text-caption">Date modified</dt>
                  <dd className="text-greyscale-text-body">
                    {formatDateTime(dateModified)}
                  </dd>
                </>
              )}
              {modifiedBy && (
                <>
                  <dt className="text-greyscale-text-caption">Modified by</dt>
                  <dd className="text-greyscale-text-body">{modifiedBy}</dd>
                </>
              )}
            </dl>
          </PopoverContent>
        </Popover>
      </div>

      {hasAnswers && (
        <Accordion type="single" collapsible className="mt-sm">
          <AccordionItem value="answers">
            <AccordionTrigger>{`Answers (${answers.length})`}</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col gap-xs">
                {answers.map((answer) => (
                  <div
                    key={answer.id ?? answer.template}
                    className="rounded-lg border border-greyscale-border-default p-sm">
                    <p className="m-none whitespace-pre-line text-sm text-greyscale-text-body">
                      {answer.template}
                    </p>
                    <div className="mt-xxs flex flex-wrap items-center gap-sm text-xs text-greyscale-text-caption">
                      <span className="inline-flex items-center gap-xxs">
                        <Calendar className="h-3 w-3" />
                        {formatDateTime(answer.date_created)}
                      </span>
                      <span className="inline-flex items-center gap-xxs">
                        <User className="h-3 w-3" />
                        ID: {answer.added_by}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

EducationQuestionCard.propTypes = {
  platform: PropTypes.string,
  category: PropTypes.string,
  template: PropTypes.string,
  isActive: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  answers: PropTypes.array,
  questionType: PropTypes.string,
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dateAdded: PropTypes.string,
  dateModified: PropTypes.string,
  modifiedBy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  addedBy: PropTypes.number,
};

export default EducationQuestionCard;
